import React, { useMemo } from 'react'
import { useSelector } from 'react-redux'
import { RootState } from '../../store'

import type { DirectLineAttachment } from '../../models/directLine'
import type { RetorikActivity } from '../../models/activityTypes'
import { CONTENT_TYPE_GRID_TITLE } from '../../models/attachmentTypes'
import { Displays } from '../../models/enums'

import SingleCardContainer from './Attachments/SingleCardContainer'
import CarouselContainer from './Attachments/CarouselContainer'
import GridContainer from '../Templates/TemplateGrid'

interface AttachmentsProps {
  activity: RetorikActivity
  display?: Displays
  history?: boolean
  triggerScroll?: () => void
}

const Attachments = ({ activity, display, history, triggerScroll }: AttachmentsProps): JSX.Element => {
  const isMobile = useSelector((state: RootState) => state.viewReducer.isMobile)

  const attachments = useMemo<Array<DirectLineAttachment>>(() => {
    return activity?.attachments?.filter((attachment) => attachment.contentType !== CONTENT_TYPE_GRID_TITLE) || []
  }, [activity.id])

  const checkDisplay = (attachmentLayout: string | undefined): Displays => {
    if (!attachments?.length) {
      return Displays.none
    }

    if (attachmentLayout) {
      switch (attachmentLayout.toLowerCase()) {
        case 'davilist':
          return Displays.none
        case 'carousel':
        case 'namedlist':
          return attachments.length > 1 ? Displays.carousel : Displays.normal
        case 'grid':
          // Grid display is not available in mobile mode
          return isMobile ? Displays.carouselgrid : Displays.grid
      }
    }

    // If we have no attachment layout but attachment(s), we use none display by default is there are several attachments
    return attachments.length > 1 ? Displays.none : Displays.normal
  }

  const currentDisplay = useMemo<Displays>(() => {
    return display || checkDisplay(activity.attachmentLayout)
  }, [attachments, display, isMobile])

  return currentDisplay === Displays.normal ? (
    <SingleCardContainer attachment={attachments[0]} history={history} activityId={activity.id} triggerScroll={triggerScroll} />
  ) : currentDisplay === Displays.carousel || currentDisplay === Displays.carouselgrid ? (
    <CarouselContainer
      title={activity.attachments?.find((attachment) => attachment.contentType === CONTENT_TYPE_GRID_TITLE)?.content?.text}
      attachments={attachments}
      history={history}
      grid={currentDisplay === Displays.carouselgrid}
      showSearchBar={!!(activity.attachmentLayout?.toLowerCase() === 'namedlist' && attachments.length > 1)}
      triggerScroll={triggerScroll}
    />
  ) : currentDisplay === Displays.grid ? (
    <GridContainer
      title={activity.attachments?.find((attachment) => attachment.contentType === CONTENT_TYPE_GRID_TITLE)?.content?.text}
      attachments={attachments}
      history={history}
      triggerScroll={triggerScroll}
    />
  ) : (
    <React.Fragment />
  )
}

export default Attachments
